import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Container } from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

type CallbackProcessingResult =
  | { status: 'success' }
  | { status: 'error'; message: string };

type LoginWithTokenHandler = (setFreshLogin?: boolean) => Promise<void>;

const INVALID_CALLBACK_MESSAGE = 'Invalid or expired authentication callback. Please sign in again.';
const SESSION_ESTABLISHMENT_MESSAGE = 'Authentication completed, but the session could not be established. Please sign in again.';
const CALLBACK_SESSION_RETRY_DELAYS_MS = [0, 400, 1000];

let pendingCallbackProcessing: Promise<CallbackProcessingResult> | null = null;
let pendingCallbackKey = '';

const decodeSearchValue = (value: string | null) => {
  if (!value) {
    return '';
  }

  try {
    return decodeURIComponent(value);
  } catch (_error) {
    return value;
  }
};

const sleep = (delayMs: number) => new Promise((resolve) => {
  window.setTimeout(resolve, delayMs);
});

const processAuthCallback = async (
  searchParams: URLSearchParams,
  loginWithToken: LoginWithTokenHandler
): Promise<CallbackProcessingResult> => {
  const error = decodeSearchValue(searchParams.get('error'));
  const errorDescription = decodeSearchValue(searchParams.get('error_description'));

  if (error) {
    return {
      status: 'error',
      message: errorDescription || error,
    };
  }

  const callbackStatus = searchParams.get('status');
  const loginProof = searchParams.get('login_proof');

  if (callbackStatus !== 'success' || !loginProof) {
    return {
      status: 'error',
      message: INVALID_CALLBACK_MESSAGE,
    };
  }

  try {
    const verificationResponse = await authAPI.verifyCallbackProof(loginProof);
    if (!verificationResponse.success || !verificationResponse.data?.valid) {
      return {
        status: 'error',
        message: INVALID_CALLBACK_MESSAGE,
      };
    }
  } catch (_error) {
    return {
      status: 'error',
      message: INVALID_CALLBACK_MESSAGE,
    };
  }

  let lastLoginErrorMessage = '';
  for (const delayMs of CALLBACK_SESSION_RETRY_DELAYS_MS) {
    if (delayMs > 0) {
      await sleep(delayMs);
    }

    try {
      await loginWithToken(true);
      return { status: 'success' };
    } catch (error) {
      if (error instanceof Error && error.message) {
        lastLoginErrorMessage = error.message;
      }
      // Retry briefly because the session cookie can arrive just after the redirect.
    }
  }

  return {
    status: 'error',
    message: lastLoginErrorMessage || SESSION_ESTABLISHMENT_MESSAGE,
  };
};

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const redirectTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const clearRedirectTimeout = () => {
      if (redirectTimeoutRef.current !== null) {
        window.clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };

    const scheduleRedirect = (path: string, delayMs: number) => {
      clearRedirectTimeout();
      redirectTimeoutRef.current = window.setTimeout(() => {
        navigate(path, { replace: true });
      }, delayMs);
    };

    const callbackKey = searchParams.toString();
    if (!pendingCallbackProcessing || pendingCallbackKey !== callbackKey) {
      pendingCallbackKey = callbackKey;
      pendingCallbackProcessing = processAuthCallback(searchParams, loginWithToken).finally(() => {
        if (pendingCallbackKey === callbackKey) {
          pendingCallbackProcessing = null;
          pendingCallbackKey = '';
        }
      });
    }

    const callbackPromise = pendingCallbackProcessing;
    callbackPromise
      .then((result) => {
        if (!isMounted) {
          return;
        }

        if (result.status === 'success') {
          setStatus('success');
          scheduleRedirect('/', 1500);
          return;
        }

        setStatus('error');
        setErrorMessage(result.message);
        scheduleRedirect(`/?error=${encodeURIComponent(result.message)}`, 3000);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setStatus('error');
        setErrorMessage(SESSION_ESTABLISHMENT_MESSAGE);
        scheduleRedirect(`/?error=${encodeURIComponent(SESSION_ESTABLISHMENT_MESSAGE)}`, 3000);
      });

    return () => {
      isMounted = false;
      clearRedirectTimeout();
    };
  }, [searchParams, navigate, loginWithToken]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            p: 6,
            textAlign: 'center',
          }}
        >
          {status === 'processing' && (
            <>
              <CircularProgress size={60} sx={{ color: '#8b5cf6', mb: 3 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Authenticating...
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we complete your sign-in
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle sx={{ fontSize: 60, color: '#10b981', mb: 3 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom color="#10b981">
                Authentication Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Redirecting to dashboard...
              </Typography>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: 60, color: '#ef4444', mb: 3 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom color="#ef4444">
                Authentication Failed
              </Typography>
              <Alert severity="error" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
                {errorMessage}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Redirecting to login page...
              </Typography>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default AuthCallback;

